import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { articles } from '@/mocks/articles';

const faqs = [
  { q: 'What is the AllChinaBuy Spreadsheet?', a: 'The AllChinaBuy Spreadsheet is an organized collection of product links, seller information, and buying resources for international shoppers.' },
  { q: 'Is AllChinaBuy legit and safe?', a: 'AllChinaBuy is a well-known buying agent. Like any international service, research sellers thoroughly and verify information before making purchases.' },
  { q: 'How do I find products in the spreadsheet?', a: 'Use the category dropdown or search function to browse products organized by type, such as shoes, hoodies, t-shirts, and accessories.' },
  { q: 'What shipping options are available?', a: 'Shipping options vary by agent and destination. Common methods include EMS, DHL, FedEx, and various postal services. Check current rates for your country.' },
];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const article = id ? articles.find((a) => a.id === id) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <article className="pt-16">
        {/* Article Header */}
        <div className="w-full px-4 sm:px-6 lg:px-10 py-12 md:py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-medium mb-6 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </button>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">{article.title}</h1>
            <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
              <span>{article.readTime}</span>
            </div>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">{article.excerpt}</p>
          </div>
        </div>

        {/* Article Body */}
        <div className="w-full px-4 sm:px-6 lg:px-10 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            {/* TOC */}
            <div className="mb-10 p-5 md:p-6 rounded-xl bg-gray-50 border border-gray-100">
              <h2 className="font-semibold text-gray-900 text-sm mb-3">Table of Contents</h2>
              <ul className="space-y-1.5">
                {article.sections.map((section, i) => (
                  <li key={i}>
                    <a href={`#section-${i}`} className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors">
                      {i + 1}. {section.heading}
                    </a>
                  </li>
                ))}
                <li>
                  <a href="#faq" className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors">FAQ</a>
                </li>
              </ul>
            </div>

            {/* Sections */}
            {article.sections.map((section, i) => (
              <div key={i} id={`section-${i}`} className="mb-10 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{section.heading}</h2>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">{section.content}</p>
              </div>
            ))}

            {/* FAQ */}
            <div id="faq" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div key={i} className="p-4 md:p-5 rounded-xl bg-gray-50 border border-gray-100">
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base mb-2">{faq.q}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Links */}
            <div className="mb-10 p-5 md:p-6 rounded-xl bg-emerald-50 border border-emerald-100">
              <h3 className="font-semibold text-emerald-800 text-sm mb-3">Related Guides</h3>
              <div className="flex flex-wrap gap-2">
                {articles.filter((a) => a.id !== id).slice(0, 8).map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/article/${a.id}`)}
                    className="px-3 py-1.5 rounded-full bg-white text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors border border-emerald-200"
                  >
                    {a.title}
                  </button>
                ))}
                <button
                  onClick={() => navigate('/')}
                  className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors"
                >
                  Back to Home
                </button>
              </div>
            </div>

            {/* CTA to main site */}
            <div className="text-center p-6 md:p-8 rounded-xl bg-gray-900 text-white">
              <h3 className="text-lg font-bold mb-2">Ready to Start Shopping?</h3>
              <p className="text-gray-400 text-sm mb-5">Explore the full AllChinaBuy spreadsheet with organized product finds and buying resources.</p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors"
              >
                Visit AllChinaBuy Spreadsheet
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100 py-8">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-400 text-xs">{t('footer.disclaimer')}</p>
            <p className="text-gray-400 text-xs mt-2">© 2026 allchinabuyspread.beer. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}